import axios from "axios";
import dayjs from "dayjs";
import React, { createContext, useContext, useState } from "react";

interface CsvContextType {
  csvData: string[][];
  readCSV: (file: File) => void;
  serverResponse: ServerResponseType | undefined;
  uploadCSVToServer: (values: number[]) => Promise<void>;
  reset: () => void;
}

type ServerResponseType = {
  predictProduct: string;
  predictScore: string;
  transmissionTime: string;
  responseTime: string;
  duration: string;
};

const CsvContext = createContext<CsvContextType | undefined>(undefined);
const requestURL = process.env.REACT_APP_API_URL as string;

export const CsvProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [serverResponse, setServerResponse] = useState<ServerResponseType>();

  const readCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = text
        .trim()
        .split("\n")
        .map((row) => row.split(",").map((cell) => cell.trim()));

      const values = rows[1]?.slice(1).map((cell) => Number(cell));

      setCsvData(rows);
      uploadCSVToServer(values);
    };

    reader.onerror = () => {
      console.log("파일을 읽는 중 오류가 발생했습니다.");
    };

    reader.readAsText(file);
  };

  const reset = () => {
    setCsvData([]);
    setServerResponse(undefined);
  };

  const uploadCSVToServer = async (values: number[]) => {
    const startTime = new Date(); // 전송 시작 시각

    try {
      const response = await axios.post(
        requestURL,
        {
          data: [values],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const endTime = new Date(); // 응답 받은 시각

      const transmissionTime = dayjs(startTime).format(
        "YYYY-MM-DD HH:mm:ss.SSS"
      ); // 전송 시작 시각
      const responseTime = dayjs(endTime).format("YYYY-MM-DD HH:mm:ss.SSS"); // 응답 종료 시각

      const duration = `${endTime.getTime() - startTime.getTime()} ms`; // 시간차 계산

      setServerResponse({
        predictProduct: response.data.predict_product,
        predictScore: response.data.predict_score,
        transmissionTime: transmissionTime,
        responseTime: responseTime,
        duration: duration,
      });

      console.log("파일이 성공적으로 전송되었습니다.");
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  return (
    <CsvContext.Provider
      value={{ csvData, readCSV, serverResponse, uploadCSVToServer, reset }}
    >
      {children}
    </CsvContext.Provider>
  );
};

export const useCsvContext = () => {
  const context = useContext(CsvContext);
  if (!context) {
    throw new Error("useCsvContext must be used within a CsvProvider");
  }
  return context;
};
